import "./App.css";
import { useEffect, useState } from "react";
import { API, COUNT_VISITOR } from "./config/api";

/* ================================
REACT ROUTER
================================ */

import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "./Client/Pages/ScrollToTop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import SearchDesigns from "./Client/Pages/SearchDesigns";
import Navbar from "./Client/Layouts/Navbar";
import Footer from "./Client/Layouts/Footer";
import Home from "./Client/Pages/Home";
import Contact from "./Client/Pages/Contact";
import Wishlist from "./Client/Pages/Wishlist";
import { MenuProvider } from "./Client/Pages/MenuContext";
import Category from "./Client/Pages/Category";
import Product from "./Client/Pages/Product";
import News from "./Client/Pages/News";
import PaymentPage from "./Client/Pages/PaymentPage";
import LatestDesign from "./Client/Pages/LatestDesign";
import Howtouse from "./Client/Pages/Howtouse";
import Ads from "./Client/Pages/Ads";
import Offer from "./Client/Pages/Offer";
import Cart from "./Client/Pages/Cart";
import MyOrders from "./Client/Pages/Orders";
import OrderDetails from "./Client/Pages/Orderdtails";
import ChangePassword from "./Client/Pages/ChangePasssword";
import Policy from "./Client/Pages/Policy";
import UsefulLink from "./Client/Pages/UsefulLink";
import Balance from "./Client/Pages/Balance";
import Services from "./Client/Pages/Services";
import Gallery from "./Client/Pages/Gallery";
import About from "./Client/Pages/About";

/* ================================
REACT QUERY
================================ */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/* ================================
LAYOUT
================================ */

function Layout() {
  const location = useLocation();

  const isUser = location.pathname.startsWith("/user");

  const isAdmin = location.pathname.startsWith("/admin");

  if (!isUser && !isAdmin) {
    return (
      <MenuProvider>
        <QueryClientProvider client={queryClient}>
          <Navbar />

          <Routes>
            <Route path="/" element={<Home />} />

            <Route path="/home" element={<Home />} />

            <Route path="/contact" element={<Contact />} />

            <Route path="/category" element={<Category />} />

            <Route path="/product" element={<Product />} />

            <Route path="/wishlist" element={<Wishlist />} />

            <Route path="/payment-methods" element={<PaymentPage />} />

            <Route path="/search" element={<SearchDesigns />} />

            <Route path="/latest-design" element={<LatestDesign />} />

            <Route path="/news" element={<News />} />

            <Route path="/ads" element={<Ads />} />

            <Route path="/how-to-use" element={<Howtouse />} />

            <Route path="/Service" element={<Services />} />

            <Route path="/offers" element={<Offer />} />

            <Route path="/cart" element={<Cart />} />

            <Route path="/myorders" element={<MyOrders />} />

            <Route path="/order-details" element={<OrderDetails />} />

            <Route path="/policy" element={<Policy />} />

            <Route path="/useful-links" element={<UsefulLink />} />

            <Route path="/balance" element={<Balance />} />
            <Route path="/about" element={<About />} />
            <Route path="/gallery" element={<Gallery />} />

            <Route
              path="/change-password/:userid"
              element={<ChangePassword />}
            />
          </Routes>

          <Footer />
        </QueryClientProvider>
      </MenuProvider>
    );
  }

  return null;
}

function App() {
  useEffect(() => {
    let counted = false;

    const countVisitor = async () => {
      if (counted) {
        return;
      }

      const today = new Date().toISOString().split("T")[0];

      /*
       * Check if visitor already counted today
       */
      const lastVisit = localStorage.getItem("visitor_date");

      if (lastVisit === today) {
        console.log("Visitor already counted today.");
        return;
      }

      counted = true;

      try {
        console.log("Visitor API:", COUNT_VISITOR);

        const response = await fetch(COUNT_VISITOR, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.json();

        console.log("Visitor API Response:", data);

        if (
          data.status === true ||
          data.status === "true" ||
          data.status === 1 ||
          data.success === true
        ) {
          localStorage.setItem("visitor_date", today);

          console.log("Visitor counted successfully.");
        } else {
          counted = false;

          console.error("Visitor count failed:", data);
        }
      } catch (error) {
        counted = false;

        console.error("Visitor API Error:", error);
      }
    };

    countVisitor();
  }, []);

  /* =====================================================
     AUTO LOGOUT - 5 HOURS
     ===================================================== */

  useEffect(() => {
    let timer;

    const logout = () => {
      localStorage.removeItem("user");

      localStorage.removeItem("token");

      window.location.href = "/";
    };

    const resetTimer = () => {
      clearTimeout(timer);

      timer = setTimeout(
        () => {
          logout();
        },
        5 * 60 * 60 * 1000,
      );
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });

      clearTimeout(timer);
    };
  }, []);

  /* =====================================================
     USER STATE
     ===================================================== */

  const [user, setUser] = useState(null);

  /* =====================================================
     LOAD USER
     ===================================================== */

  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Invalid stored user:", error);

        localStorage.removeItem("user");
      }
    }
  }, []);

  /* =====================================================
     SYNC USER
     ===================================================== */

  useEffect(() => {
    const syncUser = () => {
      const storedUser = localStorage.getItem("user");

      try {
        setUser(storedUser ? JSON.parse(storedUser) : null);
      } catch (error) {
        console.error("Invalid stored user:", error);

        setUser(null);
      }
    };

    window.addEventListener("storage", syncUser);

    return () => {
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  /* =====================================================
     AUTO LOGOUT - 2 HOURS
     ===================================================== */

  useEffect(() => {
    let timer;

    const reset = () => {
      clearTimeout(timer);

      timer = setTimeout(
        () => {
          localStorage.removeItem("user");

          localStorage.removeItem("token");

          window.location.href = "/";
        },
        2 * 60 * 60 * 1000,
      );
    };

    const events = ["mousemove", "keydown", "click", "scroll"];

    events.forEach((event) => {
      window.addEventListener(event, reset);
    });

    reset();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, reset);
      });

      clearTimeout(timer);
    };
  }, []);

  /* =====================================================
     RETURN
     ===================================================== */

  return (
    <BrowserRouter>
      <ScrollToTop />

      <Layout user={user} setUser={setUser} />
    </BrowserRouter>
  );
}

export default App;
